import { useEffect, useMemo, useState } from 'react';
import { getSupabaseRuntimeIdentity } from '../../features/runtime/supabaseAdapter';
import { getSupabaseClient } from '../../lib/supabase';

type ProgressRow = { skill_id: string; mastery_state: string; mastery_percent: number; evidence_count: number; first_pass_accuracy: number | null; independent_accuracy: number | null; average_active_seconds: number | null; last_activity_at: string | null };
type SkillRow = { id: string; code: string; learner_statement: string };
type EvidenceRow = { id: string; skill_id: string; source_id: string | null; source_type: string; evidence_type: string; first_attempt_correct: boolean | null; final_correct: boolean | null; hint_count: number; active_duration_ms: number; evidence_weight: number; occurred_at: string };
type CorrectionRow = { attempt_answer_id: string; previous_response: unknown; corrected_response: unknown; correct_after_correction: boolean };
type InkRow = { attempt_answer_id: string; stroke_count: number; point_count: number; input_type: string; duration_ms: number; capture_replay: boolean };

function responseValue(value: unknown) {
  if (!value || typeof value !== 'object') return '—';
  const row = value as Record<string, unknown>;
  return row.value === undefined ? '—' : String(row.value);
}

export function CloudStudentProgressPage() {
  const [progress, setProgress] = useState<ProgressRow[]>([]);
  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [evidence, setEvidence] = useState<EvidenceRow[]>([]);
  const [corrections, setCorrections] = useState<CorrectionRow[]>([]);
  const [inks, setInks] = useState<InkRow[]>([]);
  const [studentName, setStudentName] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const client = getSupabaseClient();
        if (!client) throw new Error('Supabase belum dikonfigurasi.');
        const identity = await getSupabaseRuntimeIdentity();
        if (!identity?.student) throw new Error('Student Record belum tersedia.');
        setStudentName(identity.student.displayName);
        const p = await client.from('student_skill_progress').select('skill_id,mastery_state,mastery_percent,evidence_count,first_pass_accuracy,independent_accuracy,average_active_seconds,last_activity_at').eq('student_id', identity.student.id).order('mastery_percent', { ascending: false });
        if (p.error) throw p.error;
        const progressRows = (p.data ?? []) as ProgressRow[];
        const ids = [...new Set(progressRows.map((row) => row.skill_id))];
        const s = ids.length ? await client.from('skills').select('id,code,learner_statement').in('id', ids) : { data: [], error: null };
        if (s.error) throw s.error;
        const ev = await client.from('skill_evidence_events').select('id,skill_id,source_id,source_type,evidence_type,first_attempt_correct,final_correct,hint_count,active_duration_ms,evidence_weight,occurred_at').eq('student_id', identity.student.id).order('occurred_at', { ascending: false }).limit(30);
        if (ev.error) throw ev.error;
        const evidenceRows = (ev.data ?? []) as EvidenceRow[];
        const answerIds = evidenceRows.map((row) => row.source_id).filter((value): value is string => Boolean(value));
        const c = answerIds.length ? await client.from('corrections').select('attempt_answer_id,previous_response,corrected_response,correct_after_correction').in('attempt_answer_id', answerIds) : { data: [], error: null };
        if (c.error) throw c.error;
        const ink = answerIds.length ? await client.from('answer_ink_records').select('attempt_answer_id,stroke_count,point_count,input_type,duration_ms,capture_replay').in('attempt_answer_id', answerIds) : { data: [], error: null };
        if (ink.error) throw ink.error;
        if (cancelled) return;
        setProgress(progressRows); setSkills((s.data ?? []) as SkillRow[]); setEvidence(evidenceRows); setCorrections((c.data ?? []) as CorrectionRow[]); setInks((ink.data ?? []) as InkRow[]);
      } catch (cause) {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Progress cloud tidak dapat dimuat.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const skillMap = useMemo(() => new Map(skills.map((row) => [row.id, row])), [skills]);
  const correctionMap = useMemo(() => new Map(corrections.map((row) => [row.attempt_answer_id, row])), [corrections]);
  const inkMap = useMemo(() => new Map(inks.map((row) => [row.attempt_answer_id, row])), [inks]);

  if (error) return <div className="feedback feedback--error">{error}</div>;

  return (
    <div className="stack">
      <div><h1>Progress Matematika</h1><p className="muted">Current record dan immutable evidence history · {studentName}</p></div>
      <section className="grid grid--3">
        <div className="metric"><span>Skill aktif</span><strong>{progress.length}</strong></div>
        <div className="metric"><span>Evidence nyata</span><strong>{evidence.length}</strong></div>
        <div className="metric"><span>Ink tersimpan</span><strong>{inks.length}</strong></div>
      </section>
      <section className="card">
        <h2>Current skill record</h2>
        <div className="skill-list">
          {progress.map((row) => {
            const skill = skillMap.get(row.skill_id);
            return <div className="skill-row" key={row.skill_id}><div><strong>{skill?.learner_statement ?? skill?.code ?? row.skill_id}</strong><div className="muted skill-code">{skill?.code}</div></div><div className="skill-mastery"><strong>{row.mastery_percent}%</strong><span className="muted">{row.mastery_state.replaceAll('_', ' ')} · evidence {row.evidence_count}</span></div></div>;
          })}
        </div>
      </section>
      <section className="card">
        <h2>History evidence terbaru</h2>
        <div className="history-list">
          {evidence.map((row) => {
            const skill = skillMap.get(row.skill_id); const correction = row.source_id ? correctionMap.get(row.source_id) : undefined; const ink = row.source_id ? inkMap.get(row.source_id) : undefined;
            return <article className="history-item" key={row.id}><div><strong>{skill?.code ?? row.skill_id}</strong><div className="muted">{new Date(row.occurred_at).toLocaleString('id-ID')} · {row.source_type}</div></div><div className="history-item__detail"><span>{row.first_attempt_correct ? '✓ benar first-attempt' : row.final_correct ? '✓ benar setelah koreksi' : '✕ belum benar'}</span><span>hint {row.hint_count}</span><span>bobot {row.evidence_weight}</span>{correction && <span>koreksi {responseValue(correction.previous_response)} → {responseValue(correction.corrected_response)}</span>}{ink && <span>✍ {ink.stroke_count} stroke / {ink.point_count} titik · {ink.input_type}{ink.capture_replay ? ' · replay' : ''}</span>}</div></article>;
          })}
          {!evidence.length && <p className="muted">Belum ada evidence nyata.</p>}
        </div>
      </section>
    </div>
  );
}
